import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withRouter } from '../../common/withRouter';
import { Notification, Message } from '../../web.common';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';

// Detail page for a single PlayEvent: stats header + editable list of sessions.
// Sessions can be added, edited (time / capacity / courts / signup deadline)
// and deleted (only when empty). Clicking "Open" navigates to the session
// detail page (/play/events/:eventId/sessions/:sessionId).
const PlayEventInternal = (props) => {
  const eventId = props.match?.params?.eventId || props.params?.eventId;
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await props.api.playEvents.get(eventId);
      setEv(data);
    } catch (e) {
      setNotification(new Message('error', `Failed to load: ${e?.message || 'unknown'}`));
    } finally {
      setLoading(false);
    }
  }, [props.api, eventId]);

  useEffect(() => { load(); }, [load]);

  const onError = (e) => setNotification(new Message('error', e?.message || 'Something went wrong'));

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!ev) return <div style={{ padding: 16 }}>Event not found.</div>;

  const sessions = ev.sessions || [];
  const totalRegistered = sessions.reduce((acc, s) => acc + (s.registeredCount || 0), 0);
  const totalCapacity = sessions.reduce((acc, s) => acc + (s.capacity || 0), 0);
  const totalWatchlist = sessions.reduce((acc, s) => acc + (s.watchlistCount || 0), 0);
  // Prefill the "add session" form with the most common values for this event.
  const defaults = {
    capacity: sessions[0]?.capacity ?? 20,
    courtCount: sessions[0]?.courtCount ?? 5,
  };

  return (
    <>
      <Notification newMessage={notification} />
      <PageTop documentTitle={ev.nameEn} documentDescription='Event detail' mainHeadingContent={ev.nameEn} />
      <div style={{ padding: 16, color: '#444', maxWidth: 1100 }}>
        <div style={{ marginBottom: 8 }}>
          <Link to='/play/events' style={{ color: '#666', fontSize: 13 }}>← Back to events</Link>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#666' }}>
            {ev.sport} · <code style={{ fontSize: 12 }}>{ev.code}</code>
            {ev.slug && <> · slug <code>{ev.slug}</code></>}
            {ev.affiliationName && <> · {ev.affiliationName}</>}
          </div>
          <div style={{ marginTop: 8, color: '#666' }}>
            {ev.locationName} · {ev.locationAddress}
          </div>

          <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
            <Stat label='Total signups' value={totalRegistered} hint={`/ ${totalCapacity}`} />
            <Stat label='Watchlist' value={totalWatchlist} />
            <Stat label='Sessions' value={sessions.length} />
            <Stat label='Status' value={ev.status} />
          </div>
        </div>

        <h3 style={{ marginTop: 24, color: '#1a3559' }}>Sessions</h3>
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left', background: '#f5f5f5' }}>
                <th style={{ padding: 10 }}>When</th>
                <th style={{ padding: 10 }}>Roster</th>
                <th style={{ padding: 10 }}>Waitlist</th>
                <th style={{ padding: 10 }}>Signup deadline</th>
                <th style={{ padding: 10 }}>Pairings</th>
                <th style={{ padding: 10 }} />
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  eventId={ev.id}
                  api={props.api}
                  onChanged={load}
                  onError={onError}
                />
              ))}
              {sessions.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 12, color: '#999' }}>No sessions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <AddSessionForm
          eventId={ev.id}
          defaults={defaults}
          api={props.api}
          onChanged={load}
          onError={onError}
        />
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Format a UTC ISO instant as "YYYY-MM-DDTHH:mm" in the admin's local
// timezone, for an <input type="datetime-local"> value.
const toLocalInputValue = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// A local datetime-local string -> UTC ISO instant (or null if empty).
const toIsoOrNull = (localValue) => (localValue ? new Date(localValue).toISOString() : null);

const linkBtn = (color) => ({
  border: 'none', background: 'none', color, cursor: 'pointer', padding: 0, fontSize: 13,
});

const Field = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#666' }}>
    {label}
    {children}
  </label>
);
Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

// Shared start/end/capacity/courts/deadline inputs used by edit + add forms.
const SessionFields = ({ form, setForm }) => {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = { fontSize: 13, padding: 4 };
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <Field label='Start (local time)'>
        <input type='datetime-local' value={form.startsAtUtc} onChange={upd('startsAtUtc')} style={input} />
      </Field>
      <Field label='End (local time)'>
        <input type='datetime-local' value={form.endsAtUtc} onChange={upd('endsAtUtc')} style={input} />
      </Field>
      <Field label='Capacity'>
        <input type='number' min='1' value={form.capacity} onChange={upd('capacity')} style={{ ...input, width: 70 }} />
      </Field>
      <Field label='Courts'>
        <input type='number' min='1' value={form.courtCount} onChange={upd('courtCount')} style={{ ...input, width: 70 }} />
      </Field>
      <Field label='Signup deadline (empty = 24h before start)'>
        <input type='datetime-local' value={form.signupDeadlineUtc} onChange={upd('signupDeadlineUtc')} style={input} />
      </Field>
    </div>
  );
};
SessionFields.propTypes = { form: PropTypes.object.isRequired, setForm: PropTypes.func.isRequired };

const buildBody = (form) => ({
  startsAtUtc: new Date(form.startsAtUtc).toISOString(),
  endsAtUtc: new Date(form.endsAtUtc).toISOString(),
  capacity: Number(form.capacity),
  courtCount: Number(form.courtCount),
  signupDeadlineUtc: toIsoOrNull(form.signupDeadlineUtc),
});

// ---------------------------------------------------------------------------
// Session row: display + inline edit + delete
// ---------------------------------------------------------------------------

const SessionRow = ({ session: s, eventId, api, onChanged, onError }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const isCustom = !!s.signupDeadlineUtc;
  const effective = s.effectiveSignupDeadlineUtc || s.signupDeadlineUtc;

  const startEdit = () => {
    setForm({
      startsAtUtc: toLocalInputValue(s.startsAtUtc),
      endsAtUtc: toLocalInputValue(s.endsAtUtc),
      capacity: String(s.capacity),
      courtCount: String(s.courtCount),
      // Explicit deadline only — empty means "use the default".
      signupDeadlineUtc: s.signupDeadlineUtc ? toLocalInputValue(s.signupDeadlineUtc) : '',
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.startsAtUtc || !form.endsAtUtc) { onError(new Error('Start and end are required')); return; }
    setBusy(true);
    try {
      await api.playEvents.updateSession(s.id, buildBody(form));
      setEditing(false);
      await onChanged();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this session? Only allowed if it has no signups, waitlist or pairings.')) return;
    setBusy(true);
    try {
      await api.playEvents.deleteSession(s.id);
      await onChanged();
    } catch (e) {
      onError(new Error(e?.message || 'Could not delete — the session may have signups, waitlist or pairings.'));
    } finally { setBusy(false); }
  };

  return (
    <>
      <tr style={{ borderBottom: editing ? 'none' : '1px solid #eee' }}>
        <td style={{ padding: 10, fontWeight: 600 }}>
          {new Date(s.startsAtUtc).toLocaleString()}{' '}
          <span style={{ color: '#999', fontWeight: 400 }}>
            – {new Date(s.endsAtUtc).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </td>
        <td style={{ padding: 10 }}>{s.registeredCount} <span style={{ color: '#999' }}>/ {s.capacity}</span></td>
        <td style={{ padding: 10 }}>{s.watchlistCount}</td>
        <td style={{ padding: 10 }}>
          <div>{effective ? new Date(effective).toLocaleString() : '—'}</div>
          <div style={{ fontSize: 11, color: '#999' }}>{isCustom ? 'custom' : 'default −24h'}</div>
        </td>
        <td style={{ padding: 10, color: s.hasActivePairings ? '#1F9D55' : '#999' }}>
          {s.hasActivePairings ? '✓ ready' : '—'}
        </td>
        <td style={{ padding: 10, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button type='button' onClick={startEdit} disabled={busy} style={linkBtn('#1a3559')}>Edit</button>
          {' · '}
          <button type='button' onClick={remove} disabled={busy} style={linkBtn('#b00020')}>Delete</button>
          {' · '}
          <Link to={`/play/events/${eventId}/sessions/${s.id}`} style={{ color: '#1a3559' }}>Open ›</Link>
        </td>
      </tr>
      {editing && (
        <tr style={{ borderBottom: '1px solid #eee', background: '#fafafa' }}>
          <td colSpan={6} style={{ padding: 12 }}>
            <SessionFields form={form} setForm={setForm} />
            <div style={{ marginTop: 8, display: 'flex', gap: 12, fontSize: 13 }}>
              <button type='button' onClick={save} disabled={busy} style={linkBtn('#1F9D55')}>Save</button>
              <button type='button' onClick={() => setEditing(false)} disabled={busy} style={linkBtn('#999')}>Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

SessionRow.propTypes = {
  session: PropTypes.object.isRequired,
  eventId: PropTypes.string,
  api: PropTypes.object.isRequired,
  onChanged: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Add session
// ---------------------------------------------------------------------------

const AddSessionForm = ({ eventId, defaults, api, onChanged, onError }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blank = {
    startsAtUtc: '', endsAtUtc: '',
    capacity: String(defaults.capacity), courtCount: String(defaults.courtCount),
    signupDeadlineUtc: '',
  };
  const [form, setForm] = useState(blank);

  const add = async () => {
    if (!form.startsAtUtc || !form.endsAtUtc) { onError(new Error('Start and end are required')); return; }
    setBusy(true);
    try {
      await api.playEvents.addSession(eventId, buildBody(form));
      setForm(blank);
      setOpen(false);
      await onChanged();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type='button'
        onClick={() => setOpen(true)}
        style={{ marginTop: 12, padding: '8px 14px', borderRadius: 6, border: '1px solid #1a3559', background: '#fff', color: '#1a3559', cursor: 'pointer', fontWeight: 600 }}
      >
        + Add session
      </button>
    );
  }

  return (
    <div style={{ marginTop: 12, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16 }}>
      <div style={{ fontWeight: 600, color: '#1a3559', marginBottom: 8 }}>New session</div>
      <SessionFields form={form} setForm={setForm} />
      <div style={{ marginTop: 10, display: 'flex', gap: 12, fontSize: 13 }}>
        <button type='button' onClick={add} disabled={busy} style={linkBtn('#1F9D55')}>Add</button>
        <button type='button' onClick={() => { setForm(blank); setOpen(false); }} disabled={busy} style={linkBtn('#999')}>Cancel</button>
      </div>
    </div>
  );
};

AddSessionForm.propTypes = {
  eventId: PropTypes.string.isRequired,
  defaults: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
  onChanged: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

const Stat = ({ label, value, hint }) => (
  <div style={{ minWidth: 140 }}>
    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: '#1a3559' }}>
      {value}
      {hint && <span style={{ fontSize: 13, fontWeight: 400, color: '#999' }}> {hint}</span>}
    </div>
  </div>
);
Stat.propTypes = { label: PropTypes.string, value: PropTypes.any, hint: PropTypes.string };

PlayEventInternal.propTypes = { api: PropTypes.object.isRequired };

export default connect(() => ({}))(withRouter(withApi(PlayEventInternal)));
